/**
 * Fires once a live catalog has replaced the baked one.
 *
 * The overlay is IN PLACE — same array references, so anything already holding `TEXT_MODELS` sees
 * the new contents. But a framework that re-renders on identity change sees nothing change at all,
 * so a picker would sit on baked, unpriced rows until something unrelated forced an update. This is
 * how a view learns to look again.
 */
export const catalogEvents = new EventTarget();
export const REALTIME_MODELS = [
    { value: 'gpt-realtime', label: 'gpt-realtime', provider: 'openai', ctx: 32_000, files: true },
    { value: 'gpt-realtime-mini', label: 'gpt-realtime mini', provider: 'openai', ctx: 32_000, files: true },
    { value: 'gpt-4o-realtime-preview', label: 'gpt-4o Realtime', provider: 'openai', ctx: 128_000, files: true },
    { value: 'gemini-2.5-flash-live', label: '2.5 Flash Live', provider: 'google', ctx: 1_000_000, files: true },
    { value: 'gemini-2.0-flash-live', label: '2.0 Flash Live', provider: 'google', ctx: 1_000_000, files: true },
    { value: 'grok-voice', label: 'Grok Voice', provider: 'xai', ctx: 128_000 },
];
/** Text/reasoning models offered for specialists, the deep-reasoning default, and the ban list. */
export const TEXT_MODELS = [
    // Movendi (the platform's own names; the worker maps each one to the model that serves it)
    { value: 'movendi/bolt', label: 'Bolt', provider: 'movendi', ctx: 1_050_000, files: true, reasoning: true },
    { value: 'movendi/isaac', label: 'Isaac', provider: 'movendi', ctx: 1_050_000, files: true, reasoning: true },
    // Anthropic
    { value: 'claude-fable-5-1', label: 'Claude Fable 5.1', provider: 'anthropic', ctx: 1_000_000, files: true, reasoning: true },
    { value: 'claude-fable-5', label: 'Claude Fable 5', provider: 'anthropic', ctx: 1_000_000, files: true, reasoning: true },
    { value: 'claude-opus-5', label: 'Claude Opus 5', provider: 'anthropic', ctx: 1_000_000, files: true, reasoning: true },
    { value: 'claude-opus-4-8', label: 'Claude Opus 4.8', provider: 'anthropic', ctx: 1_000_000, files: true, reasoning: true },
    { value: 'claude-opus-4-7', label: 'Claude Opus 4.7', provider: 'anthropic', ctx: 1_000_000, files: true, reasoning: true },
    { value: 'claude-opus-4-6', label: 'Claude Opus 4.6', provider: 'anthropic', ctx: 1_000_000, files: true, reasoning: true },
    { value: 'claude-sonnet-5', label: 'Claude Sonnet 5', provider: 'anthropic', ctx: 1_000_000, files: true, reasoning: true },
    { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6', provider: 'anthropic', ctx: 1_000_000, files: true, reasoning: true },
    { value: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5', provider: 'anthropic', ctx: 1_000_000, files: true, reasoning: true },
    { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5', provider: 'anthropic', ctx: 200_000, files: true, reasoning: true },
    // OpenAI
    { value: 'gpt-5', label: 'GPT-5', provider: 'openai', ctx: 400_000, files: true, reasoning: true },
    { value: 'gpt-5-mini', label: 'GPT-5 mini', provider: 'openai', ctx: 400_000, files: true, reasoning: true },
    { value: 'gpt-5-nano', label: 'GPT-5 nano', provider: 'openai', ctx: 400_000, files: true, reasoning: true },
    { value: 'gpt-4.1', label: 'GPT-4.1', provider: 'openai', ctx: 1_000_000, files: true },
    { value: 'gpt-4.1-mini', label: 'GPT-4.1 mini', provider: 'openai', ctx: 1_000_000, files: true },
    { value: 'gpt-4.1-nano', label: 'GPT-4.1 nano', provider: 'openai', ctx: 1_000_000, files: true },
    { value: 'gpt-4o', label: 'GPT-4o', provider: 'openai', ctx: 128_000, files: true },
    { value: 'gpt-4o-mini', label: 'GPT-4o mini', provider: 'openai', ctx: 128_000, files: true },
    { value: 'o3', label: 'o3', provider: 'openai', ctx: 200_000, files: true, reasoning: true },
    { value: 'o3-mini', label: 'o3-mini', provider: 'openai', ctx: 200_000, reasoning: true },
    { value: 'o4-mini', label: 'o4-mini', provider: 'openai', ctx: 200_000, files: true, reasoning: true },
    // Google
    { value: 'gemini-2.5-pro', label: '2.5 Pro', provider: 'google', ctx: 1_000_000, files: true, reasoning: true },
    { value: 'gemini-2.5-flash', label: '2.5 Flash', provider: 'google', ctx: 1_000_000, files: true, reasoning: true },
    { value: 'gemini-flash-latest', label: 'Flash (latest)', provider: 'google', ctx: 1_000_000, files: true },
    { value: 'gemini-flash-lite-latest', label: 'Flash Lite (latest)', provider: 'google', ctx: 1_000_000, files: true },
    // xAI
    { value: 'grok-4', label: 'Grok 4', provider: 'xai', ctx: 256_000, files: true, reasoning: true },
    { value: 'grok-4-fast', label: 'Grok 4 Fast', provider: 'xai', ctx: 2_000_000, files: true },
    { value: 'grok-code-fast-1', label: 'Grok Code Fast', provider: 'xai', ctx: 256_000 },
];
/**
 * IMAGE-OUTPUT models — the ones that hand back a picture rather than prose.
 *
 * A THIRD ROSTER, not a filter on the text one, and the live catalogue is what settles it: not one
 * image model declares tool support, so every one of them is excluded from the text roster by the
 * rule that a model which cannot call a tool cannot serve a conversation here. The two lists are
 * disjoint, and a picker that filtered the text list by modality would show nothing forever.
 *
 * Short on purpose. Two families generate images at a price worth paying, and a picker full of
 * near-duplicates is one nobody can choose from.
 */
export const IMAGE_MODELS = [
    { value: 'google/gemini-3-pro-image', label: 'Nano Banana Pro', provider: 'google', sub: 'highest fidelity' },
    { value: 'google/gemini-3.1-flash-image', label: 'Nano Banana 2', provider: 'google', sub: 'fast, cheap' },
    { value: 'google/gemini-2.5-flash-image', label: 'Nano Banana', provider: 'google' },
    { value: 'openai/gpt-5-image', label: 'GPT-5 Image', provider: 'openai' },
    { value: 'openai/gpt-5-image-mini', label: 'GPT-5 Image mini', provider: 'openai', sub: 'fast, cheap' },
];
/**
 * Overlay the worker's catalog onto the baked arrays, in place.
 *
 * `baseUrl` is the AI plane's origin. It defaults to same-origin, which is right for the console
 * (served beside the worker) and wrong everywhere else — the gateway app talks to a different
 * origin entirely, and a relative fetch there would ask its OWN server for a route it does not
 * have, fail quietly, and leave the picker on stale baked prices with nothing to show for it.
 *
 * Last-known-good on every failure path: a bad shape or an offline plane keeps the baked copy.
 * Returns the raw snapshot so a caller can overlay parts this module does not own.
 */
export async function loadModelCatalog(baseUrl = '') {
    try {
        // no-store: the catalog is small and the WORKER caches it server-side; letting the browser
        // HTTP-cache it (the response carries max-age) means a normal reload keeps serving a stale
        // roster/prices for up to that window without re-fetching. Always pull fresh on page load.
        const res = await fetch(`${baseUrl}/api/model-catalog`, { cache: 'no-store' });
        if (!res.ok)
            return null;
        const cat = (await res.json());
        const gotRealtime = Array.isArray(cat.realtime) && cat.realtime.length > 0;
        const gotText = Array.isArray(cat.text) && cat.text.length > 0;
        // Each rail is overlaid INDEPENDENTLY. A deployment holding a key that serves text and not
        // images (or the reverse) gets a live roster for the one and the baked fallback for the other,
        // rather than one empty rail dragging the others down with it.
        const gotImage = Array.isArray(cat.image) && cat.image.length > 0;
        if (gotRealtime)
            REALTIME_MODELS.splice(0, REALTIME_MODELS.length, ...cat.realtime);
        if (gotText)
            TEXT_MODELS.splice(0, TEXT_MODELS.length, ...cat.text);
        if (gotImage)
            IMAGE_MODELS.splice(0, IMAGE_MODELS.length, ...cat.image);
        if (gotRealtime || gotText || gotImage)
            catalogEvents.dispatchEvent(new CustomEvent('change'));
        return cat;
    }
    catch {
        return null; // offline / plane unreachable → baked fallback stands
    }
}
/**
 * Find a model by id, ACROSS THE TWO ID NAMESPACES that legitimately coexist.
 *
 * The live text roster comes from OpenRouter and is vendor-namespaced (`x-ai/grok-4.20`). The
 * realtime roster, the baked fallback, and anything an org has already SAVED use bare ids
 * (`gemini-2.5-pro`) — because a natively-routed model does not go through the aggregator and has no
 * namespace to carry. Both are correct; they are just written differently.
 *
 * A strict `===` therefore fails to resolve a perfectly valid saved default the moment the live
 * roster loads, and the UI falls back to a generic word — which reads as "nothing is configured"
 * when something very much is. Compares the bare id when either side carries a vendor prefix.
 */
export function findModel(models, id) {
    if (!id)
        return undefined;
    const exact = models.find((m) => m.value === id);
    if (exact)
        return exact;
    const bare = (v) => v.slice(v.lastIndexOf('/') + 1);
    const want = bare(id);
    return models.find((m) => bare(m.value) === want);
}
//# sourceMappingURL=model-catalog.js.map